import { Injectable, inject } from '@angular/core';
import { RotorCompareService } from './rotor-compare.service';
import { RotorApi, RotorForComparison } from '../api';
import { downloadFile } from '@schenck/rotec-components';
import { SettingsService } from './settings.service';
import { take, switchMap, tap } from 'rxjs/operators';
import { isEmpty } from 'lodash';
import { ExportUserSettings } from '../utils/interfaces/export-settings.interface';

@Injectable()
export class DownloadService {
  private readonly compareService = inject(RotorCompareService);
  private readonly settingsService = inject(SettingsService);
  private readonly rotorApi = inject(RotorApi);

  /**
   * Does a download of given ids as PDF.
   */
  downloadAsPDF(ids: string[], userSettings?: ExportUserSettings) {
    return this.settingsService.settings$
      .pipe(
        take(1),
        switchMap((settings) => {
          return isEmpty(userSettings)
            ? this.rotorApi.exportPDF({
                ids,
                logo: settings.protocolSettings.protocolLogo,
                language: settings.protocolSettings.protocolLanguage,
                decimals: settings.unitSettings.decimals,
                units: settings.unitSettings.units,
                //commentField: settings.protocolSettings.commentField, // Comment box in the cloud and on the machine does not affect in the report. It will be hide the checkbox unless we have implemented a workable solution.
                signatureField: settings.protocolSettings.signatureField,
                measuringRuns: settings.protocolSettings.measuringRuns,
              })
            : this.rotorApi.exportPDF({
                ...userSettings,
                ids,
              });
        })
      )
      .pipe(
        tap((response) =>
          downloadFile(response, this.getFileNameFromIds(ids), 'pdf', 'application/pdf')
        )
      );
  }

  /**
   * Does a download of given ids as CSV.
   * Used when CSV export button in pages banner is clicked
   */
  downloadAsCSV(ids: string[]) {
    return this.rotorApi
      .exportCSV(ids)
      .pipe(
        tap((response) =>
          downloadFile(response, this.getFileNameFromIds(ids), 'csv', 'text/csv;charset=utf-8')
        )
      );
  }
  /**
   * Does a download of given ids as CSV.
   * Used when CSV button in grid is clicked
   */

  /**
   * Returns file name from given id(s).
   * Only works when rotors are selected and therefore stored in the compare service state.
   */
  getFileNameFromIds(ids: string[]) {
    const id = ids.length === 1 ? (this.compareService.state.rotors[ids[0]]?.rotorId ?? '') : '';
    const rotorTypes = ids
      .map((_id) => this.compareService.state.rotors[_id]?.typeName)
      .filter((typeName, pos, self) => self.indexOf(typeName) === pos)
      .join('-');
    return this.getFileName(id, rotorTypes);
  }

  /**
   * Returns file name from given rotor.
   * Also works if no rotors are selected because rotor information needs to be passed.
   */
  getFileNameFromRotor(rotor: RotorForComparison) {
    return this.getFileName(rotor.rotorId, rotor.typeName);
  }

  /**
   * Generates the whole file name.
   */
  getFileName(id: string, rotorTypes: string): string {
    const date = new Date();

    return `${date.getUTCFullYear()}-${date.getMonth() < 9 ? '0' : ''}${date.getMonth() + 1}-${
      date.getDate() < 10 ? '0' : ''
    }${date.getDate()}_${date.getHours() < 10 ? '0' : ''}${date.getHours()}-${
      date.getMinutes() < 10 ? '0' : ''
    }${date.getMinutes()}${id === '' ? '' : '_'}${id}_${rotorTypes}`.substring(0, 200);
  }
}
